import { useState, useEffect, useMemo } from 'react'
import { useTableColumns, type UseTableColumnsResult } from '@/hooks/useTableColumns'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Loader2,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  Users,
  Plus,
  MoreVertical,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { procedureTemplatesApi } from '@/api/procedureTemplatesApi'
import { usePatients } from '@/hooks/usePatients'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { formatAge, formatDate, formatSex, patientName, patientNameParts } from '@/lib/patient'
import { latestProcedureFirst, procedureStatusMeta } from '@/lib/procedureStatus'
import type { PatientProcedureWithProgress, PatientListItem } from '@/api/patientsApi'
import { useBackNavLinkBuilder } from '@/components/navigation/BackLink'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PageHeading } from '@/components/layout/PageHeading'
import { sortParam } from '@/lib/tableSort'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

// ─── Table ─────────────────────────────────────────────────────────────────────

/** One procedure: status pill, name, step progress. */
function ProcedureLine({ p }: { p: PatientProcedureWithProgress }) {
  const meta = procedureStatusMeta(p)
  const { progress } = p
  const pct = progress && progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0
  return (
    <div className='flex items-center gap-2 text-xs'>
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium',
          meta.badge,
        )}
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', meta.dot)} />
        {meta.label}
      </span>
      {/* Not flex-1: letting the name stretch pushes the progress bar to the
          far edge of the cell, away from the procedure it describes. */}
      <span className='min-w-0 max-w-[10rem] truncate' title={p.procedureTemplate.name}>
        {p.procedureTemplate.name}
      </span>
      {progress && progress.total > 0 ? (
        <span className='flex shrink-0 items-center gap-1.5'>
          <span className='h-1 w-8 overflow-hidden rounded-full bg-slate-200'>
            <span className='block h-full rounded-full bg-emerald-500' style={{ width: `${pct}%` }} />
          </span>
          <span className='tabular-nums text-[11px] text-muted-foreground'>
            {progress.completed}/{progress.total}
          </span>
        </span>
      ) : (
        <span className='shrink-0 text-[11px] text-muted-foreground/60'>No steps</span>
      )}
    </div>
  )
}

/**
 * The patient's procedures, each with its status and step progress. Both come
 * preloaded on the list response — scoring them per row on the client is the
 * fan-out this page was rebuilt to remove.
 *
 * One line per row: the latest procedure, then "+N more", which lists them all
 * in a popover. A patient with many runs used to stretch their row down the
 * page and push the rest of the list out of view.
 */
function ProceduresCell({ procedures }: { procedures: PatientProcedureWithProgress[] }) {
  const { latest, rest } = latestProcedureFirst(procedures)
  if (!latest) {
    return <span className='text-xs text-muted-foreground'>No procedures</span>
  }
  return (
    <div className='flex items-center gap-2'>
      <div className='min-w-0'>
        <ProcedureLine p={latest} />
      </div>
      {rest.length > 0 && (
        <Popover>
          {/* The row opens the patient on click and Enter; this opens the list. */}
          <PopoverTrigger asChild>
            <button
              type='button'
              className='shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              aria-label={`Show all ${procedures.length} procedures`}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
            >
              +{rest.length} more
            </button>
          </PopoverTrigger>
          <PopoverContent
            align='start'
            className='w-auto max-w-[28rem] p-3'
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <p className='mb-2 text-xs font-semibold text-muted-foreground'>
              All procedures ({procedures.length}), latest first
            </p>
            <div className='flex max-h-72 flex-col gap-1.5 overflow-y-auto pr-1'>
              {[latest, ...rest].map(p => (
                <ProcedureLine key={p.id} p={p} />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}

// Per-user column layout. MRN is locked (it identifies the row and the e2e
// locator matches on it) and so is the chevron, which is the row's open
// affordance. See src/components/table/index.ts for the pattern.
const PATIENTS_TABLE_ID = 'patients-overview'

// `sortable` columns sort SERVER-side (z8nrz7e4h6): this list is paged, so the
// ids here are the backend's allowed sort keys (PATIENT_SORTS in
// patients.service). Procedures is a list per patient, so it has no one value
// to order by.
const PATIENTS_COLUMNS: TableColumnDef[] = [
  { id: 'mrn', header: 'MRN', locked: true, sortable: true },
  { id: 'firstName', header: 'First name', sortable: true },
  { id: 'lastName', header: 'Last name', sortable: true },
  { id: 'procedures', header: 'Procedures' },
  { id: 'age', header: 'Age', sortable: true },
  { id: 'sex', header: 'Sex', sortable: true },
  { id: 'created', header: 'Created', sortable: true },
  { id: 'open', header: 'Open', locked: true },
]

// The pinned widths and alignment this table already relied on, kept per column
// so the header renders exactly as it did before it became configurable.
const PATIENT_HEAD_CLASSES: Record<string, string> = {
  mrn: 'w-80 px-4 py-3 font-medium',
  firstName: 'w-40 px-4 py-3 font-medium',
  lastName: 'w-40 px-4 py-3 font-medium',
  procedures: 'w-[22rem] px-4 py-3 font-medium',
  age: 'w-44 px-4 py-3 font-medium',
  sex: 'w-24 px-4 py-3 font-medium',
  created: 'px-4 py-3 text-right font-medium',
  open: 'w-12 px-4 py-3',
}

function PatientTable({
  rows,
  cols,
  onOpen,
}: {
  rows: PatientListItem[]
  /** Owned by the page, whose query needs the sort (z8nrz7e4h6). */
  cols: UseTableColumnsResult
  onOpen: (mrn: string) => void
}) {
  return (
    <>
    <div className='overflow-x-persistent rounded-xl border bg-card'>
      <table className='w-full text-sm'>
        <thead className='border-b bg-table-header text-left text-xs text-table-header-foreground'>
          {/* A full-width table with sparse content has surplus width to put
              somewhere: auto-layout spreads it over every column (which separated
              MRN from Name), and pinning all but one dumps it into that one.
              So the content columns are pinned to fit their longest realistic
              value, and Created absorbs the remainder with its text right-aligned
              — the surplus becomes a clean right margin instead of a mid-table
              gulf. MRN is sized for the long synthetic E2E identifiers.

              Those pinned widths now live on each column's `headClassName`, and a
              width the user has dragged overrides them inline (see ResizableTh).
              The chevron column keeps no header text, so its menu entry is named
              by `header` alone. */}
          <tr>
            {cols.visible.map(({ def }) => (
              <ResizableTh
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className={PATIENT_HEAD_CLASSES[def.id]}
                resizable={def.id !== 'open'}
              >
                {def.id === 'open' ? null : def.header}
              </ResizableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => {
            const name = patientName(p)
            const { first, last } = patientNameParts(p)
            return (
              <tr
                key={p.mrn}
                role='link'
                tabIndex={0}
                aria-label={`Open patient ${name ?? p.mrn}`}
                onClick={() => onOpen(p.mrn)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen(p.mrn)
                  }
                }}
                className={cn(
                  'cursor-pointer border-b align-top transition-colors last:border-0 hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                  i % 2 === 1 && 'bg-muted/10',
                )}
              >
                {/* The e2e patient locator matches rows by their visible MRN text. */}
                <ColumnCells
                  columns={cols}
                  cells={{
                    mrn: (
                      <td className='whitespace-nowrap px-4 py-3 font-mono text-xs font-medium'>
                        {p.mrn}
                      </td>
                    ),
                    // Absent demographics render as an empty cell rather than
                    // a repeated "not recorded" string: most MRN-only rows would
                    // carry four of them, which reads as noise instead of
                    // information.
                    firstName: <td className='px-4 py-3'>{first}</td>,
                    lastName: <td className='px-4 py-3'>{last}</td>,
                    procedures: (
                      <td className='px-4 py-3'>
                        <ProceduresCell procedures={p.procedures} />
                      </td>
                    ),
                    age: (
                      <td className='whitespace-nowrap px-4 py-3 text-xs text-muted-foreground'>
                        {formatAge(p.birthDate)}
                      </td>
                    ),
                    sex: <td className='px-4 py-3 text-xs text-muted-foreground'>{formatSex(p.sex)}</td>,
                    created: (
                      <td className='whitespace-nowrap px-4 py-3 text-right text-xs text-muted-foreground'>
                        {formatDate(p.createdAt)}
                      </td>
                    ),
                    open: (
                      <td className='px-4 py-3'>
                        <ChevronRight className='h-4 w-4 text-muted-foreground/40' />
                      </td>
                    ),
                  }}
                />
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
    </>
  )
}

// ─── Page ───────────────────────────────────────────────────────────────────────

export function PatientsOverviewPage() {
  const navigate = useNavigate()
  // Its own live url, so the journey page can return to this list with its
  // search and paging intact (z8nrz7czj8).
  const withOrigin = useBackNavLinkBuilder('Patients')
  const { can } = usePermission()

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [procedureTemplateFilter, setProcedureTemplateFilter] = useState<string>('all')
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(20)
  const [actionsOpen, setActionsOpen] = useState(false)
  const [syncMsg, setSyncMsg] = useState<string | null>(null)

  // Debounce the search box, and drop back to page 1 when the query changes —
  // staying on page 5 of a narrower result set shows an empty table.
  //
  // Guarded on the values themselves rather than a "first run" ref: StrictMode
  // invokes effects twice on mount, which defeats a ref flag and let the timer fire
  // 300ms after load, resetting skip and snapping any paging in that window back to
  // page 1. When query already equals debouncedQuery there is nothing to debounce.
  useEffect(() => {
    if (query === debouncedQuery) return
    const id = setTimeout(() => {
      setDebouncedQuery(query)
      setSkip(0)
    }, 300)
    return () => clearTimeout(id)
  }, [query, debouncedQuery])

  // Page resets happen in the control handlers, NOT in an effect keyed on the
  // control's value. An effect resets skip one render late, so a Next click landing
  // between the page-size change and the effect was silently rolled back to page 1.

  // A new sort is a new ordering of the whole list, so page 1 again (z8nrz7e4h6).
  // Reset during render, for the same one-render-late reason as above.
  const cols = useTableColumns(PATIENTS_TABLE_ID, PATIENTS_COLUMNS)
  const sort = sortParam(cols.sort)
  const [sortOfPage, setSortOfPage] = useState(sort)
  if (sortOfPage !== sort) {
    setSortOfPage(sort)
    setSkip(0)
  }

  const params = useMemo(
    () => ({
      q: debouncedQuery || undefined,
      procedureTemplateId: procedureTemplateFilter !== 'all' ? procedureTemplateFilter : undefined,
      skip,
      take,
      sort,
    }),
    [debouncedQuery, procedureTemplateFilter, skip, take, sort],
  )

  const { list, sync } = usePatients(params)

  // Filter options come from the procedure templates catalogue. They used to be
  // derived from an all-procedures fetch, which only ever offered templates some
  // patient was already on.
  const { data: procedureTemplates = [] } = useQuery({
    queryKey: ['procedure-templates', 'list'],
    queryFn: () => procedureTemplatesApi.list(),
  })

  const patients = list.data?.items ?? []
  const total = list.data?.total ?? 0
  const trimmed = query.trim()
  const error = list.error ?? sync.error

  function openPatient(mrn: string) {
    navigate(withOrigin(`/patients/${encodeURIComponent(mrn)}`))
  }

  // Carry whatever was typed in the search box over as the MRN seed.
  function openCreatePatient() {
    navigate(`/patients/new${trimmed ? `?mrn=${encodeURIComponent(trimmed)}` : ''}`)
  }

  async function handleSync() {
    setActionsOpen(false)
    setSyncMsg(null)
    const res = await sync.mutateAsync().catch(() => null)
    if (res) {
      setSyncMsg(
        `Synced: linked ${res.proceduresLinked} procedure(s), created ${res.patientsCreated} patient(s).`,
      )
    }
  }

  return (
    <div className='min-h-full bg-muted/20'>
      <div className='px-6 py-8'>

        {/* Header */}
        <div className='mb-6 flex items-center justify-between gap-4'>
          <PageHeading title='Patients' description='Every registered patient and the procedures opened for them.' />
          <div className='flex items-center gap-2'>
            {/* Hidden without procedures.manage, which the create route and the
                backend's POST /patients both require — showing it otherwise just
                routes the user to /no-access. */}
            {can(PERMISSIONS.proceduresManage) && (
              <Button size='sm' onClick={openCreatePatient}>
                <Plus className='mr-2 h-4 w-4' />
                New patient
              </Button>
            )}
            <Button
              variant='ghost'
              size='sm'
              onClick={() => void list.refetch()}
              disabled={list.isFetching}
            >
              {list.isFetching ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Refresh'}
            </Button>
            <div className='relative'>
              <Button
                variant='ghost'
                size='icon'
                className='h-8 w-8'
                aria-label='More actions'
                onClick={() => setActionsOpen(v => !v)}
              >
                <MoreVertical className='h-4 w-4' />
              </Button>
              {actionsOpen && (
                <>
                  <div className='fixed inset-0 z-40' onClick={() => setActionsOpen(false)} />
                  <div className='absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-md border bg-popover py-1 text-sm shadow-md'>
                    <button
                      className='flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted disabled:opacity-50'
                      disabled={sync.isPending}
                      onClick={() => void handleSync()}
                    >
                      {sync.isPending ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <RefreshCw className='h-4 w-4' />
                      )}
                      Sync patients with procedure
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {syncMsg && (
          <div className='mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-2.5 text-sm text-green-700'>
            {syncMsg}
          </div>
        )}

        {/* Search / filter */}
        <div className='mb-5 flex gap-2'>
          <div className='relative flex-1'>
            <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
            <Input
              className='pl-9'
              placeholder='Search existing patients by MRN or name…'
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <Select
            value={procedureTemplateFilter}
            onValueChange={v => {
              setProcedureTemplateFilter(v)
              setSkip(0)
            }}
          >
            <SelectTrigger className='w-60 shrink-0'>
              <SelectValue placeholder='Filter by procedure' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All procedures</SelectItem>
              {procedureTemplates.map(p => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <div className='mb-4 flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
            <AlertCircle className='h-4 w-4 shrink-0' />
            {error instanceof Error ? error.message : 'Failed to load patients.'}
          </div>
        )}

        {list.isPending && (
          <div className='flex items-center justify-center py-24'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        )}

        {!list.isPending && total === 0 && (
          <div className='rounded-xl border border-dashed bg-card py-16 text-center'>
            <Users className='mx-auto mb-3 h-10 w-10 text-muted-foreground/30' />
            <p className='text-sm text-muted-foreground'>
              {trimmed ? `No patient matches "${trimmed}".` : 'No patients yet.'}
            </p>
          </div>
        )}

        {!list.isPending && patients.length > 0 && (
          <PatientTable rows={patients} cols={cols} onOpen={openPatient} />
        )}

        {!list.isPending && total > 0 && (
          <div className='mt-3 flex items-center justify-between rounded-lg border bg-card px-4 py-2 text-xs text-muted-foreground'>
            <span>
              {skip + 1}–{Math.min(skip + take, total)} of {total} patients
            </span>
            <div className='flex items-center gap-2'>
              <select
                value={take}
                onChange={e => {
                  setTake(Number(e.target.value))
                  setSkip(0)
                }}
                className='rounded border bg-background px-2 py-0.5 text-xs'
              >
                {PAGE_SIZE_OPTIONS.map(n => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
              <div className='flex items-center gap-1'>
                <button
                  className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                  disabled={skip === 0}
                  onClick={() => setSkip(s => Math.max(0, s - take))}
                >
                  <ChevronLeft className='h-3.5 w-3.5' />
                  Previous
                </button>
                <button
                  className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                  disabled={skip + take >= total}
                  onClick={() => setSkip(s => s + take)}
                >
                  Next
                  <ChevronRight className='h-3.5 w-3.5' />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
